//* Libraries imports
import { Row, Col, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import { useState, useMemo, useCallback } from "react";
import {
  FaGraduationCap,
  FaUser,
  FaIdCard,
  FaChartLine,
  FaTrophy,
  FaBullseye,
  FaCertificate,
  FaDownload,
  FaEye,
} from "react-icons/fa";
import { ArrowLeft } from "lucide-react";

//* Components imports
import { CertificateModal } from "./view-resul-modal";
import Icon from "../../../../components/common/Icon";

//* Types imports
import { FullResult } from "../../../../schemas/common/result/result";

//* Styles imports
import styles from "./view-result-student-info.module.scss";

// Constants extracted for better maintainability
const ANIMATION_CONFIG = {
  containerVariants: {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  },
  itemVariants: {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  },
  iconAnimation: {
    rotate: { duration: 0.8, ease: "easeOut" as const },
    scale: { duration: 0.5, delay: 0.3 },
  },
  progressAnimation: { duration: 1, delay: 0.5 },
} as const;

const PERFORMANCE_CONFIG = {
  THRESHOLDS: {
    HIGH: 70,
    MEDIUM: 50,
  },
  MESSAGES: {
    HIGH: "Excelente desempenho!",
    MEDIUM: "Bom desempenho",
    LOW: "Continue estudando",
  },
  TYPES: {
    HIGH: "high-score",
    MEDIUM: "medium-score",
    LOW: "low-score",
  },
} as const;

const UI_CONFIG = {
  ICON_SIZES: {
    SMALL: 14,
    MEDIUM: 16,
    LARGE: 20,
    XLARGE: 24,
    XXLARGE: 28,
  },
  INITIALS_COUNT: 2,
  DEFAULT_FILENAME: "certificado.pdf",
} as const;

type ViewResultStudentInfoProps = {
  estudante: FullResult["resultado"]["estudante"];
  desempenho_prova: FullResult["resultado"]["desempenho_prova"];
  certificado?: FullResult["resultado"]["estudante"]["certificado"];
  onBack: () => void;
};

// Helper functions extracted for better testability and reusability
const getPerformanceData = (percentage: number) => {
  if (percentage >= PERFORMANCE_CONFIG.THRESHOLDS.HIGH) {
    return {
      type: PERFORMANCE_CONFIG.TYPES.HIGH,
      message: PERFORMANCE_CONFIG.MESSAGES.HIGH,
    };
  }
  if (percentage >= PERFORMANCE_CONFIG.THRESHOLDS.MEDIUM) {
    return {
      type: PERFORMANCE_CONFIG.TYPES.MEDIUM,
      message: PERFORMANCE_CONFIG.MESSAGES.MEDIUM,
    };
  }
  return {
    type: PERFORMANCE_CONFIG.TYPES.LOW,
    message: PERFORMANCE_CONFIG.MESSAGES.LOW,
  };
};

const getExamTypeInfo = (desempenho: FullResult["resultado"]["desempenho_prova"]) => {
  switch (desempenho.tipo_prova) {
    case "objetiva":
      return {
        label: "Prova Objetiva",
        details: `${desempenho.questoes_objetivas.corretas} de ${desempenho.questoes_objetivas.total} questões corretas`,
        specificPercentage: desempenho.questoes_objetivas.taxa_acerto,
      };
    case "dissertativa":
      return {
        label: "Prova Dissertativa",
        details: `${desempenho.questoes_dissertativas.corrigidas} de ${desempenho.questoes_dissertativas.total} questões corrigidas`,
        specificPercentage: desempenho.questoes_dissertativas.taxa_correcao,
        averageGrade: desempenho.questoes_dissertativas.nota,
      };
    default:
      // TypeScript exhaustiveness check
      const _exhaustiveCheck: never = desempenho;
      return _exhaustiveCheck;
  }
};

const generateStudentInitials = (name: string): string => {
  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .slice(0, UI_CONFIG.INITIALS_COUNT)
    .join("")
    .toUpperCase();
};

const createDownloadLink = (
  url: string,
  filename: string,
  ariaLabel: string
) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || UI_CONFIG.DEFAULT_FILENAME;
  link.setAttribute("aria-label", ariaLabel);
  return link;
};

export function ViewResultStudentInfo({
  estudante,
  desempenho_prova,
  certificado,
  onBack,
}: ViewResultStudentInfoProps) {
  const [showCertificateModal, setShowCertificateModal] = useState(false);  
  const performanceData = useMemo(() => {
    const percentage = desempenho_prova.taxa_acerto_geral;
    const { type, message } = getPerformanceData(percentage);
    const examTypeInfo = getExamTypeInfo(desempenho_prova);

    return {
      taxaAcertoPercentual: percentage,
      performanceType: type,
      performanceMessage: message,
      examTypeInfo,
    };
  }, [desempenho_prova]);

  // Memoized student initials - O(n) where n is word count
  const studentInitials = useMemo(
    () => generateStudentInitials(estudante.nome),
    [estudante.nome]
  );

  // Enhanced certificate download with better error handling
  const handleDownloadCertificate = useCallback(() => {
    if (!certificado?.url) {
      console.error("Certificate URL not available");
      return;
    }

    try {
      const ariaLabel = `Baixar certificado de ${estudante.nome}`;
      const link = createDownloadLink(
        certificado.url,
        certificado.filename || UI_CONFIG.DEFAULT_FILENAME,
        ariaLabel
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading certificate:", error);
    }
  }, [certificado, estudante.nome]);

  const handleCloseModal = useCallback(() => {
    setShowCertificateModal(false);
  }, []);

  const handleOpenModal = useCallback(() => {
    setShowCertificateModal(true);
  }, []);

  // Component render - extracted sections for better readability
  const renderHeader = () => (
    <motion.div
      className={styles["result-card-header"]}
      variants={ANIMATION_CONFIG.itemVariants}
    >
      <div className={styles["modern-header"]}>
        {/* Back Button */}
        <motion.div
          className={styles["back-button-container"]}
          variants={ANIMATION_CONFIG.itemVariants}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            variant="light"
            onClick={onBack}
            className={styles["modern-back-button"]}
            aria-label="Voltar para página anterior"
          >
            <motion.div
              className={styles["button-content"]}
              initial={{ x: 0 }}
              whileHover={{ x: -2 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <ArrowLeft
                size={UI_CONFIG.ICON_SIZES.LARGE}
                className={styles["back-icon"]}
              />
              <span className={styles["back-text"]}>Voltar</span>
            </motion.div>
          </Button>
        </motion.div>

        {/* Title Section */}
        <motion.div
          className={styles["title-section"]}
          variants={ANIMATION_CONFIG.itemVariants}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className={styles["title-content"]}>
            <motion.div
              className={styles["icon-wrapper"]}
              initial={{ rotate: 0, scale: 0.8 }}
              animate={{ rotate: 360, scale: 1 }}
              transition={ANIMATION_CONFIG.iconAnimation}
              role="img"
              aria-label="Ícone de formatura"
            >
              <Icon icon={FaGraduationCap} size={UI_CONFIG.ICON_SIZES.XXLARGE} color="white" />
            </motion.div>
            <div className={styles["title-text"]}>
              <motion.h1
                className={styles["main-title"]}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                Resultados do Candidato {estudante.nome}
              </motion.h1>
              <motion.p
                className={styles["title-subtitle"]}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                Análise completa do desempenho
              </motion.p>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  const renderCertificateSection = () => {
    if (!certificado) return null;

    return (
      <motion.div
        className={styles["certificate-section"]}
        variants={ANIMATION_CONFIG.itemVariants}
      >
        <motion.div
          className={styles["certificate-banner"]}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          whileHover={{ scale: 1.02 }}
          role="banner"
          aria-label="Seção de certificado disponível"
        >
          <div className={styles["certificate-content"]}>
            <div className={styles["certificate-icon"]} aria-hidden="true">
              <Icon icon={FaCertificate} size={UI_CONFIG.ICON_SIZES.XLARGE} />
            </div>
            <div className={styles["certificate-text"]}>
              <h4 className={styles["certificate-title"]}>
                🎉 Certificado Disponível!
              </h4>
              <p className={styles["certificate-subtitle"]}>
                Parabéns! Seu certificado está pronto para download
              </p>
            </div>
            <div className={styles["certificate-actions"]}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleOpenModal}
                className={styles["preview-btn"]}
                aria-label="Visualizar certificado"
              >
                <Icon icon={FaEye} size={UI_CONFIG.ICON_SIZES.SMALL} className="me-1" />
                Visualizar
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={handleDownloadCertificate}
                className={styles["download-btn"]}
                aria-label="Baixar certificado"
              >
                <Icon icon={FaDownload} size={UI_CONFIG.ICON_SIZES.SMALL} className="me-1" />
                Baixar Certificado
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  const renderStudentInfo = () => (
    <motion.div
      className={styles["student-section"]}
      variants={ANIMATION_CONFIG.itemVariants}
    >
      <div className={styles["student-header"]}>
        <div
          className={styles.avatar}
          aria-label={`Avatar de ${estudante.nome}`}
          role="img"
        >
          {studentInitials}
        </div>
        <div className={styles["student-info"]}>
          <h2 className={styles["student-name"]}>{estudante.nome}</h2>
          <p className={styles["student-subtitle"]}>Candidato</p>
        </div>
      </div>

      <div className={styles["student-details"]}>
        <Row>
          <Col md={6}>
            <motion.div
              className={styles["detail-item"]}
              variants={ANIMATION_CONFIG.itemVariants}
            >
              <div className={styles.icon} aria-hidden="true">
                <Icon icon={FaUser} size={UI_CONFIG.ICON_SIZES.MEDIUM} />
              </div>
              <div className={styles.content}>
                <p className={styles.label}>CPF</p>
                <p className={styles.value}>{estudante.cpf}</p>
              </div>
            </motion.div>
          </Col>

          <Col md={6}>
            <motion.div
              className={styles["detail-item"]}
              variants={ANIMATION_CONFIG.itemVariants}
            >
              <div className={styles.icon} aria-hidden="true">
                <Icon icon={FaIdCard} size={UI_CONFIG.ICON_SIZES.MEDIUM} />
              </div>
              <div className={styles.content}>
                <p className={styles.label}>Número de Inscrição</p>
                <p className={styles.value}>{estudante.numero_inscricao}</p>
              </div>
            </motion.div>
          </Col>
        </Row>
      </div>
    </motion.div>
  );
  const renderPerformanceStats = () => (
    <motion.div
      className={styles["performance-section"]}
      variants={ANIMATION_CONFIG.itemVariants}
    >
      <div className={styles["section-header"]}>
        <div className={styles["icon-wrapper"]} aria-hidden="true">
          <Icon icon={FaChartLine} size={UI_CONFIG.ICON_SIZES.LARGE} />
        </div>
        <h3 className={styles["section-title"]}>Desempenho</h3>
        <span className={styles["exam-type-badge"]}>
          {performanceData.examTypeInfo.label}
        </span>
      </div>

      <div className={styles["performance-stats"]}>
        <Row>
          <Col md={4}>
            <motion.div
              className={`${styles["stat-card"]} ${styles["score-card"]}`}
              variants={ANIMATION_CONFIG.itemVariants}
            >
              <div className={styles["stat-header"]}>
                <span className={styles["stat-label"]}>Total de Questões</span>
                <div className={styles["stat-icon"]} aria-hidden="true">
                  <Icon icon={FaBullseye} size={UI_CONFIG.ICON_SIZES.SMALL} />
                </div>
              </div>
              <h4 className={styles["stat-value"]}>
                {desempenho_prova.total_questoes}
              </h4>
              <p className={styles["stat-subtitle"]}>questões na prova</p>
            </motion.div>
          </Col>

          <Col md={4}>
            <motion.div
              className={`${styles["stat-card"]} ${styles["score-card"]}`}
              variants={ANIMATION_CONFIG.itemVariants}
            >
              <div className={styles["stat-header"]}>
                <span className={styles["stat-label"]}>Acertos</span>
                <div className={styles["stat-icon"]} aria-hidden="true">
                  <Icon icon={FaBullseye} size={UI_CONFIG.ICON_SIZES.SMALL} />
                </div>
              </div>
              <h4 className={styles["stat-value"]}>
                {desempenho_prova.respostas_corretas}
              </h4>
              <p className={styles["stat-subtitle"]}>
                {performanceData.examTypeInfo.details}
              </p>
            </motion.div>
          </Col>

          <Col md={4}>
            <motion.div
              className={`${styles["stat-card"]} ${styles["percentage-card"]} ${styles[performanceData.performanceType]}`}
              variants={ANIMATION_CONFIG.itemVariants}
            >              <div className={styles["stat-header"]}>
                <span className={styles["stat-label"]}>Taxa de Acerto Geral</span>
                <div className={styles["stat-icon"]} aria-hidden="true">
                  <Icon icon={FaTrophy} size={UI_CONFIG.ICON_SIZES.SMALL} />
                </div>
              </div>
              <h4 className={styles["stat-value"]}>
                {performanceData.taxaAcertoPercentual.toFixed(1)}%
              </h4>
              <p className={styles["stat-subtitle"]}>
                {performanceData.performanceMessage}
              </p>

              <div
                className={`${styles["progress-bar"]} ${styles[performanceData.performanceType]}`}
              >
                <motion.div
                  className={styles["progress-fill"]}
                  initial={{ width: 0 }}
                  animate={{ width: `${performanceData.taxaAcertoPercentual}%` }}
                  transition={ANIMATION_CONFIG.progressAnimation}
                  role="progressbar"
                  aria-valuenow={performanceData.taxaAcertoPercentual}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Taxa de acerto: ${performanceData.taxaAcertoPercentual.toFixed(
                    1
                  )}%`}
                />
              </div>
            </motion.div>
          </Col>
        </Row>

        {/* Additional performance details for specific exam types */}
        {desempenho_prova.tipo_prova === "dissertativa" && (
          <Row className="mt-3">
            <Col md={6}>
              <motion.div
                className={`${styles["stat-card"]} ${styles["detail-card"]}`}
                variants={ANIMATION_CONFIG.itemVariants}
              >
                <div className={styles["stat-header"]}>
                  <span className={styles["stat-label"]}>Nota</span>
                </div>
                <h4 className={styles["stat-value"]}>
                  {desempenho_prova.questoes_dissertativas.nota.toFixed(1)}
                </h4>
              </motion.div>
            </Col>
            
            <Col md={6}>
              <motion.div
                className={`${styles["stat-card"]} ${styles["detail-card"]}`}
                variants={ANIMATION_CONFIG.itemVariants}
              >
                <div className={styles["stat-header"]}>
                  <span className={styles["stat-label"]}>Processo de Correção</span>
                </div>
                <h4 className={styles["stat-value"]}>
                  {desempenho_prova.questoes_dissertativas.taxa_correcao.toFixed(1)}%
                </h4>
                <p className={styles["stat-subtitle"]}>
                  {desempenho_prova.questoes_dissertativas.pendentes} pendentes
                </p>
              </motion.div>
            </Col>
          </Row>
        )}
      </div>
    </motion.div>
  );

  return (
    <>
      <motion.div
        className={styles["result-card"]}
        variants={ANIMATION_CONFIG.containerVariants}
        initial="hidden"
        animate="visible"
        role="main"
        aria-label="Resultados do candidato"
      >
        {renderHeader()}

        <div className={styles["result-card-body"]}>
          <Row>
            {/* Certificate section first */}
            {certificado && (
              <>
                <Col lg={12}>{renderCertificateSection()}</Col>

                <Col lg={12}>
                  <div className={styles.spacer} aria-hidden="true" />
                </Col>
              </>
            )}

            {/* Student info second */}
            <Col lg={12}>{renderStudentInfo()}</Col>

            <Col lg={12}>
              <div className={styles.spacer} aria-hidden="true" />
            </Col>

            {/* Performance stats last */}
            <Col lg={12}>{renderPerformanceStats()}</Col>
          </Row>
        </div>
      </motion.div>

      <CertificateModal
        show={showCertificateModal}
        onHide={handleCloseModal}
        certificate={certificado || null}
        studentName={estudante.nome}
        onDownload={handleDownloadCertificate}
      />
    </>
  );
}
